<template>
	<ion-loading :is-open="migrationInProgress" :message="migrationStepText"></ion-loading>
</template>

<script setup>
import { IonLoading } from "@ionic/vue"
import { onMounted, ref } from "vue"
import { storeToRefs } from "pinia"
import { useSettingsStore } from "@/stores/settings"
import { useVersionStore } from "@/stores/version"
import { useApiStore } from "@/stores/api"
import { useRouteStore } from "@/stores/route"
import { sleep, compareVersions } from "@/helpers/util"

const { mapVisibleTrackers } = storeToRefs(useSettingsStore())
const { isBetaTester, isLoggedIn } = storeToRefs(useApiStore())
const { appVersion, migrationsDone, migrationInProgress } = storeToRefs(useVersionStore())
const { reset, startSyncIfNeeded } = useRouteStore()
const version = ref("1.7.2")

const migrationStepText = ref("")

onMounted(async () => {
	if (appVersion.value == version.value) {
		migrationInProgress.value = false
		return
	}

	if (appVersion.value == null) {
		console.log("🆕 no version set. Setting " + version.value)
		appVersion.value = version.value
	}

	// migrations 1.1.0
	if (compareVersions(appVersion.value, "1.1.0") < 0) {
		console.log("🆕 [1.1.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."

		if (isLoggedIn.value == true) {
			console.log("🆕 [1.1.0] user logged in, migrating ...")
			// migrate route style settings
			migrationStepText.value = "Aktualisiere Einstellungen..."
			for (const visibleTracker of mapVisibleTrackers.value) {
				visibleTracker.routeStyle = "default.default"
			}

			// setting to beta tester user
			migrationStepText.value = "Setze Benutzerstatus..."
			isBetaTester.value = true

			// migrate positionlogs database schema
			// resync is needed because of changed field list
			migrationStepText.value = "Leere Routen-Cache..."
			await reset()

			await sleep(2000)
			migrationStepText.value = "Starte Synchronisierung..."

			startSyncIfNeeded()
		} else {
			console.log("🆕 [1.1.0] user not logged in, nothing to migrate.")
		}
		console.log("🆕 [1.1.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.1.0"
	}

	// migrations 1.1.1
	if (compareVersions(appVersion.value, "1.1.1") < 0) {
		console.log("🆕 [1.1.1] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.1.1] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.1.1"
	}

	// migrations 1.2.0
	if (compareVersions(appVersion.value, "1.2.0") < 0) {
		console.log("🆕 [1.2.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.2.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.2.0"
	}

	// migrations 1.3.0
	if (compareVersions(appVersion.value, "1.3.0") < 0) {
		console.log("🆕 [1.3.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.3.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.3.0"
	}

	// migrations 1.3.1
	if (compareVersions(appVersion.value, "1.3.1") < 0) {
		console.log("🆕 [1.3.1] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.3.1] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.3.1"
	}

	// migrations 1.3.2
	if (compareVersions(appVersion.value, "1.3.2") < 0) {
		console.log("🆕 [1.3.2] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.3.2] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.3.2"
	}

	// migrations 1.4.0
	if (compareVersions(appVersion.value, "1.4.0") < 0) {
		console.log("🆕 [1.4.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.0"
	}

	// migrations 1.4.1
	if (compareVersions(appVersion.value, "1.4.1") < 0) {
		console.log("🆕 [1.4.1] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.1] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.1"
	}

	// migrations 1.4.2
	if (compareVersions(appVersion.value, "1.4.2") < 0) {
		console.log("🆕 [1.4.2] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.2] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.2"
	}

	// 1.4.3 was never published.

	// migrations 1.4.4
	if (compareVersions(appVersion.value, "1.4.4") < 0) {
		console.log("🆕 [1.4.4] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.4] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.4"
	}

	// migrations 1.4.5
	if (compareVersions(appVersion.value, "1.4.5") < 0) {
		console.log("🆕 [1.4.5] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.5] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.5"
	}

	// migrations 1.4.6
	if (compareVersions(appVersion.value, "1.4.6") < 0) {
		console.log("🆕 [1.4.6] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.6] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.6"
	}

	// migrations 1.4.7
	if (compareVersions(appVersion.value, "1.4.7") < 0) {
		console.log("🆕 [1.4.7] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.7] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.7"
	}

	// migrations 1.4.8
	if (compareVersions(appVersion.value, "1.4.8") < 0) {
		console.log("🆕 [1.4.8] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.4.8] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.4.8"
	}

	// migrations 1.5.0
	if (compareVersions(appVersion.value, "1.5.0") < 0) {
		console.log("🆕 [1.5.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.0"
	}

	// migrations 1.5.1
	if (compareVersions(appVersion.value, "1.5.1") < 0) {
		console.log("🆕 [1.5.1] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.1] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.1"
	}

	// migrations 1.5.2
	/*
		Change log:
		- added tour detail view selected style beeing saved in settings
		- improved new popups, changed styling (no more green labels) and fixed bug in tracker-popup
		- improved gpx export
		- modified tooltip fade out delay for charts
		- added fullscreen mode for tour map
		- added early access shareng feature

		[internal]
		- added readme.md
		- improved building process, automated sentry integration and added custom build commands
	*/
	if (compareVersions(appVersion.value, "1.5.2") < 0) {
		console.log("🆕 [1.5.2] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.2] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.2"
	}

	// migrations 1.5.3
	/*
		Change log:
		- fixed bugs in shareng user interface
		- fixed keyboard bug in ios26
		- fixed missing blur effect on map page
		- improved chart 

		[internal]
		- trying to get sentry right now. 
		- fixed problem, where userdata was not loaded when app was auto-logged-in
		- trying to fix network connection errors thrown to sentry (timeout)
		- fixed thrown exception when share sheet is cancelled on export

	*/
	if (compareVersions(appVersion.value, "1.5.3") < 0) {
		console.log("🆕 [1.5.3] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.3] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.3"
	}

	// migrations 1.5.4
	/*
		Change log:
		- rolled out shareng feature
		- fixes a problem, where text wrap was ugly in tracker detail modal
		- fixed missing form validation errors
		- added "save to documents" button for export modal in android

		[internal]
		- fixed a bug, where exception was thrown wenn center button was clicked at zero visible locations


	*/
	if (compareVersions(appVersion.value, "1.5.4") < 0) {
		console.log("🆕 [1.5.4] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.4] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.4"
	}

	// migrations 1.5.5
	/*
		Change log:
		- improved routing, probably fixed strange tabbar behaviour
		- fixed missing critical alerts request on ios
		- added settings for coordinates format

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.5.5") < 0) {
		console.log("🆕 [1.5.5] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.5] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.5"
	}

	// migrations 1.5.6
	/*
		Change log:
		- fixed broken push notifications on iOS

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.5.6") < 0) {
		console.log("🆕 [1.5.6] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.6] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.6"
	}

	// migrations 1.5.7
	/*
		Change log:
		- added telemetry data and chart
		- persisting overnight view settings in tour details view

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.5.7") < 0) {
		console.log("🆕 [1.5.7] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.7] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.7"
	}

	// migrations 1.5.8
	/*
		Change log:
		- added feedback modal
		- improved chart scaling for telemetry data

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.5.8") < 0) {
		console.log("🆕 [1.5.8] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.8] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.8"
	}

	// migrations 1.5.9
	/*
		Change log:
		- fixed typo
		- fixed overnights beeing shown out of selected boundaries
		- improved feedback form / public release version
		- added sorting for markers, markercategories and tours
		- improved routing for places
		- added dms coordinate parsing when pasting marker location
		- fixed some sentry errors which improved error handling and stability
		- fixed missing loading bar in map tab

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.5.9") < 0) {
		console.log("🆕 [1.5.9] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.5.9] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.5.9"
	}

	// migrations 1.6.0
	/*
		Change log:
		- fixed sharing iframe code bug
		- fixed bug, where old tracking data from beta test time could not be clicked on in the map
		- ui: added toggle overnight visibility button in tour detail "..."-menu
		- improved loading-bar behaviour
		- fixed labeling bug on tour segmented route style
		- improved date picker usability and behaviour
		- added more timeframe presets for map filter menu
		- improved styling and usability of filter menu
		- added voltage field for export dialog
		- added quick analyze feature in tour tab

		[internal]
		- switched to SPM packages (SQLite is installed manually in xcode)

	*/
	if (compareVersions(appVersion.value, "1.6.0") < 0) {
		console.log("🆕 [1.6.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.6.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.6.0"
	}

	// migrations 1.6.1
	/*
		Change log:
		- fixed bug, where quick analyze modal didnt work when date was entered manually
		- fixed a bug, where zero speed records could be shown with < > buttons
		- added field validation for coodinates in marker form
		- catched some errors from sentry

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.6.1") < 0) {
		console.log("🆕 [1.6.1] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.6.1] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.6.1"
	}

	// migrations 1.6.2
	/*
		Change log:
		- improved segmented route styles, which are now always connected and more precise
		- fixed bug, where markers could not be created by long tapping on the map
		- improved back button behaviour on android (back button closes when nothing to go back to)
		- improved menu placement on big screens (especially android tablets with navigation ("hardware-")buttons)

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.6.2") < 0) {
		console.log("🆕 [1.6.2] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.6.2] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.6.2"
	}

	// migrations 1.6.3
	/*
		Change log:
		- fixed a problem in segmented routes, where the label data was 1 off
		- made "edit tour" button visible when tour has no data - it was hidden by mistake
		- added delete buttons in the navigation bar in views where it was missing
		- added auto syncing when app is activated from background

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.6.3") < 0) {
		console.log("🆕 [1.6.3] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.6.3] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.6.3"
	}

	// migrations 1.6.4
	/*
		Change log:
		- fixed a bug with next/prev record selecting in some rare scenarios
		- improved tour fullscreen mode (sidebar is hidden on large screens)
		- improved performance of gradient route styles
		- improved performance and stability of map rendering/rerendering and on device sleep
		- fixed bug where calandar disappeard in some views randomly

		[internal]
		- upgraded to capacitor 8 and ionic v9
		- replaced capacitor-autofill-password-plugin

	*/
	if (compareVersions(appVersion.value, "1.6.4") < 0) {
		console.log("🆕 [1.6.4] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.6.4] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.6.4"
	}

	// migrations 1.7.0
	/*
		Change log:
		- added images feature
		- cleaned up app settings and "more" menu

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.7.0") < 0) {
		console.log("🆕 [1.7.0] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.7.0] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.7.0"
	}

	// migrations 1.7.1
	/*
		Change log:
		- massive cleanup in code and design
		- alarm page overhaul
		- new "low_ext_voltage" alarm
		- added links to marker note field
		- improved performance in tours view and in general
		- added overlaying tours styling in map view
		- fixed alarm sound not working on android
		- added button to jump to photolibrary from image slider (android only)

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.7.1") < 0) {
		console.log("🆕 [1.7.1] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.7.1] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.7.1"
	}

	// migrations 1.7.2
	/*
		Change log:
		- fixed bug on iOS 26 where app got inresponsive after selecting alarm tab
		- fixed typo
		- fixed bug, where map was not always centered on selected marker
		- made google maps button visible on tracker detail view without expansion

		[internal]

	*/
	if (compareVersions(appVersion.value, "1.7.2") < 0) {
		console.log("🆕 [1.7.2] starting migration...")
		migrationInProgress.value = true
		migrationStepText.value = "Upgrade wird gestartet..."
		console.log("🆕 [1.7.2] migrating done, setting version to " + version.value)
		// migration is done
		migrationStepText.value = "Fertig."
		migrationsDone.value.push(version.value)
		appVersion.value = "1.7.2"
	}

	migrationInProgress.value = false
})
</script>
